// src/components/navigation/Sidebar.tsx
import { useState, useRef, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Phone,
  PhoneCall,
  Link as LinkIcon,
  Building2,
  User,
  LogOut,
  Mail,
  MessageSquare,
  Globe,
  Map,
  ListTodo,
  CreditCard,
  Bell,
  Clock,
  Zap,
  Users,
  X,
  HelpCircle,
  Contact,
  TrendingUp,
  Settings,
  UserPlus,
  Languages,
  BarChart3,
  Brain,
  Star,
  Shield,
  Plus,
  ChevronDown,
  Inbox,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useBranding } from '@/contexts/BrandingContext';
import { useUserRole } from '@/hooks/useUserRole';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import FoundingMemberBadge from '@/components/ui/FoundingMemberBadge';
import { isPartnerUser } from '@/utils/partnerUtils';
import { useUnreadAlerts } from '@/hooks/useUserAlerts';
import { useBillingUsage } from '@/hooks/useBilling';
import { useAIEmployees } from '@/hooks/useAIEmployeeQueries';
import { useSmsUnreadCount } from '@/hooks/useSmsUnreadCount';
import { usePartnerDetails } from '@/hooks/usePartnerDetails';
import { getHelpFeedbackHref } from '@/utils/helpFeedback';
import MemoizedLogo from '@/components/branding/MemoizedLogo';
import SidebarSection from './SidebarSection';

interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
}

interface NavGroup {
  key: string;
  label: string;
  icon: LucideIcon;
  defaultOpen: boolean;
  items: NavItem[];
}

const navigationGroups: NavGroup[] = [
  {
    key: 'communications',
    label: 'Communications',
    icon: MessageSquare,
    defaultOpen: true,
    items: [
      { name: 'Scheduled Calls', href: '/dashboard/call-queue', icon: ListTodo },
      { name: 'Past Calls', href: '/dashboard/calls', icon: Phone },
      { name: 'AI Call Summaries', href: '/dashboard/call-summaries', icon: Brain },
      { name: 'AI Emails', href: '/dashboard/emails', icon: Mail },
      { name: 'AI SMS', href: '/dashboard/sms', icon: MessageSquare },
      { name: 'Conversations', href: '/dashboard/crm/inbox', icon: Inbox },
      { name: 'Follow-Ups', href: '/dashboard/follow-ups', icon: Zap },
    ],
  },
  {
    key: 'growth',
    label: 'Growth',
    icon: TrendingUp,
    defaultOpen: true,
    items: [
      { name: 'Booking Pages', href: '/dashboard/booking-pages', icon: Globe },
      { name: 'CRM', href: '/dashboard/crm', icon: Contact },
      { name: 'Workflows', href: '/dashboard/workflows', icon: Zap },
      { name: 'Reviews', href: '/dashboard/reviews', icon: Star },
      { name: 'ROI Report', href: '/dashboard/roi', icon: BarChart3 },
    ],
  },
  {
    key: 'configuration',
    label: 'Configuration',
    icon: Settings,
    defaultOpen: false,
    items: [
      { name: 'Business Overview', href: '/dashboard/business-overview', icon: Building2 },
      { name: 'AI Employees', href: '/dashboard/ai-employees', icon: Users },
      { name: 'Language Settings', href: '/dashboard/language-settings', icon: Languages },
      { name: 'Integrations', href: '/dashboard/integrations', icon: LinkIcon },
    ],
  },
  {
    key: 'account',
    label: 'Account',
    icon: User,
    defaultOpen: false,
    items: [
      { name: 'Billing', href: '/dashboard/billing', icon: CreditCard },
      { name: 'Account', href: '/dashboard/account', icon: User },
      { name: 'Learning Center', href: '/dashboard/resources', icon: Map },
    ],
  },
];

const NEW_MENU_ITEMS = [
  { label: 'Schedule a Call', icon: Phone, href: '/dashboard/call-queue' },
  { label: 'Send Message', icon: MessageSquare, href: '/dashboard/sms' },
  { label: 'Add Contact', icon: UserPlus, href: '/dashboard/crm' },
];

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

export default function Sidebar({ isOpen = false, onClose }: SidebarProps) {
  const { signOut, user, isFoundingMember, profile } = useAuth();
  const { branding, isWhiteLabel } = useBranding();
  const userRole = useUserRole();
  const { isAdmin } = useIsAdmin();
  const navigate = useNavigate();
  const { data: alerts } = useUnreadAlerts();
  const { data: billingUsage } = useBillingUsage();
  const { data: employees } = useAIEmployees();
  const smsUnread = useSmsUnreadCount();

  const [showNewMenu, setShowNewMenu] = useState(false);
  const [showEmployeePicker, setShowEmployeePicker] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const newMenuRef = useRef<HTMLDivElement>(null);
  const employeePickerRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (showNewMenu && newMenuRef.current && !newMenuRef.current.contains(e.target as Node)) {
        setShowNewMenu(false);
      }
      if (showEmployeePicker && employeePickerRef.current && !employeePickerRef.current.contains(e.target as Node)) {
        setShowEmployeePicker(false);
      }
      if (showUserMenu && userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showNewMenu, showEmployeePicker, showUserMenu]);

  const handleLogoutClick = async () => {
    setShowUserMenu(false);
    try {
      const { error } = await signOut();
      if (error) throw error;
      toast.success('Logged out successfully');
      navigate('/login', { replace: true });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Logout failed: ${errorMessage}`);
      console.error('Logout error from sidebar:', error);
    }
  };

  const handleTestEmployee = (employeeName: string) => {
    setShowEmployeePicker(false);
    window.dispatchEvent(new CustomEvent('start-ai-test', { detail: { employeeName } }));
  };

  const handleTestAIClick = () => {
    const activeEmployees = employees?.filter(e => e.is_active) || [];
    if (activeEmployees.length <= 1) {
      window.dispatchEvent(new Event('start-ai-test'));
    } else {
      setShowEmployeePicker(prev => !prev);
    }
  };

  const userDisplayName = profile?.full_name || user?.email;
  const unreadCount = alerts?.length || 0;
  const isPartner = isPartnerUser(profile?.user_type);
  const { data: partnerDetails } = usePartnerDetails();
  const helpFeedbackHref = getHelpFeedbackHref({
    userType: profile?.user_type,
    partnerId: profile?.partner_id,
    partnerType: partnerDetails?.partner_type,
  });

  const partnerNavigation = [
    { name: 'Partner', href: '/partner/dashboard', icon: Users },
    { name: 'Billing', href: '/dashboard/billing', icon: CreditCard },
    { name: 'Account', href: '/dashboard/account', icon: User },
    ...(userRole !== 'whitelabel_user' ? [{ name: 'Learning Center', href: '/dashboard/resources', icon: Map }] : []),
  ];

  const handleNavClick = () => {
    if (onClose) onClose();
  };

  const roleColors: Record<string, string> = {
    'Receptionist': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    'Sales Agent': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    'Support Specialist': 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
    'After-Hours Agent': 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    'Custom': 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  };

  // User initials for avatar
  const userInitials = profile?.full_name
    ? profile.full_name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)
    : user?.email?.[0]?.toUpperCase() || '?';

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div className={`
        flex flex-col h-full w-64 bg-muted border-r border-border
        md:relative md:translate-x-0
        fixed left-0 top-0 z-50 transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        {/* Close button for mobile */}
        <div className="md:hidden absolute top-4 right-4 z-10">
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8 text-muted-foreground hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Logo */}
        <div className="flex items-center justify-center px-4 py-3 border-b border-border">
          <MemoizedLogo
            logoUrl={branding.logoUrl}
            companyName={branding.companyName}
            isWhiteLabel={isWhiteLabel}
            className="h-16 w-auto max-w-full object-contain"
          />
        </div>

        {/* "+ New" Button */}
        {!isPartner && (
          <div className="px-3 pt-3 pb-1" ref={newMenuRef}>
            <div className="relative">
              <button
                onClick={() => setShowNewMenu(prev => !prev)}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors shadow-sm"
              >
                <Plus className="h-4 w-4" />
                New
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showNewMenu ? 'rotate-180' : ''}`} />
              </button>

              {showNewMenu && (
                <div className="absolute top-full left-0 right-0 mt-1 rounded-lg border border-border bg-background shadow-lg z-50 overflow-hidden">
                  {NEW_MENU_ITEMS.map((item) => (
                    <button
                      key={item.label}
                      onClick={() => {
                        setShowNewMenu(false);
                        navigate(item.href);
                        handleNavClick();
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-foreground hover:bg-accent transition-colors"
                    >
                      <item.icon className="h-4 w-4 text-muted-foreground" />
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Navigation */}
        {isPartner ? (
          <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
            {partnerNavigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                end
                className={({ isActive }) =>
                  `flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-[#7087a9] hover:bg-accent'
                  }`
                }
                onClick={handleNavClick}
              >
                <item.icon className="mr-3 h-5 w-5 flex-shrink-0" aria-hidden="true" />
                <span>{item.name}</span>
              </NavLink>
            ))}
          </nav>
        ) : (
          <nav className="flex-1 px-2 py-2 overflow-y-auto">
            {/* Dashboard - always visible top-level */}
            <NavLink
              to="/dashboard/dashboard"
              end
              className={({ isActive }) =>
                `flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${
                  isActive
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-[#7087a9] hover:bg-accent'
                }`
              }
              onClick={handleNavClick}
            >
              <LayoutDashboard className="mr-3 h-5 w-5 flex-shrink-0" />
              <span>Dashboard</span>
            </NavLink>

            {/* Grouped navigation sections */}
            {navigationGroups.map((group) => (
              <SidebarSection
                key={group.key}
                title={group.label}
                icon={group.icon}
                storageKey={group.key}
                defaultOpen={group.defaultOpen}
                childHrefs={group.items.map((i) => i.href)}
              >
                {group.items.map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    end={!['crm', 'sms', 'booking-pages', 'ai-employees'].some(sub => item.href.endsWith(sub))}
                    className={({ isActive }) =>
                      `flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${
                        isActive
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : 'text-[#7087a9] hover:bg-accent'
                      }`
                    }
                    onClick={handleNavClick}
                  >
                    <item.icon className="mr-3 h-5 w-5 flex-shrink-0" />
                    <span>{item.name}</span>
                    {item.href === '/dashboard/crm/inbox' && smsUnread > 0 && (
                      <Badge variant="secondary" className="ml-auto h-5 min-w-5 justify-center px-1.5 text-xs">
                        {smsUnread > 99 ? '99+' : smsUnread}
                      </Badge>
                    )}
                  </NavLink>
                ))}
              </SidebarSection>
            ))}

            {/* Admin link */}
            {isAdmin && (
              <div className="mt-2 pt-2 border-t border-border">
                <NavLink
                  to="/admin/dashboard"
                  className={({ isActive }) =>
                    `flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${
                      isActive
                        ? 'bg-orange-600 text-white shadow-sm'
                        : 'text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-950/20'
                    }`
                  }
                  onClick={handleNavClick}
                >
                  <Shield className="mr-3 h-5 w-5 flex-shrink-0" />
                  <span>Admin Panel</span>
                </NavLink>
              </div>
            )}
          </nav>
        )}

        {/* Bottom Section */}
        <div className="border-t border-border">
          {/* Free Trial Indicator */}
          {billingUsage?.freeTrialInfo?.isInFreeTrial && (
            <div className={`mx-4 mt-3 rounded-lg p-3 border ${
              (billingUsage.freeTrialInfo.daysRemaining || 0) <= 3
                ? "bg-orange-50 border-orange-200 dark:bg-orange-950/20 dark:border-orange-800"
                : "bg-blue-50 border-blue-200 dark:bg-blue-950/20 dark:border-blue-800"
            }`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <Zap className={`h-4 w-4 mr-2 ${
                    (billingUsage.freeTrialInfo.daysRemaining || 0) <= 3 ? "text-orange-500" : "text-blue-500"
                  }`} />
                  <span className="text-sm font-medium">
                    {billingUsage.freeTrialInfo.trialType === 'stripe' ? 'Trial Active' : 'Free Credits'}
                  </span>
                </div>
                <Badge
                  variant={(billingUsage.freeTrialInfo.daysRemaining || 0) <= 3 ? "destructive" : "secondary"}
                  className="text-xs"
                >
                  {billingUsage.freeTrialInfo.daysRemaining || 0} days left
                </Badge>
              </div>
              <div className="flex items-center text-xs text-muted-foreground">
                <Clock className="h-3 w-3 mr-1" />
                {billingUsage.freeTrialInfo.trialType === 'stripe'
                  ? 'Plan activates automatically'
                  : 'Choose plan to continue'
                }
              </div>
            </div>
          )}

          {/* Test AI Employee CTA */}
          {!isPartner && (
            <div className="px-4 pt-3" ref={employeePickerRef}>
              <div className="relative">
                <button
                  onClick={handleTestAIClick}
                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg border border-primary/20 bg-primary/5 text-primary text-sm font-medium hover:bg-primary/10 transition-colors"
                >
                  <PhoneCall className="h-4 w-4" />
                  Test AI Employee
                </button>

                {/* Employee Picker */}
                {showEmployeePicker && (
                  <div className="absolute bottom-full left-0 right-0 mb-2 rounded-lg border border-border bg-background shadow-lg z-50">
                    <div className="p-2">
                      <p className="text-xs font-medium text-muted-foreground px-2 py-1.5">Select AI Employee</p>
                      {(employees || []).filter(e => e.is_active).map((emp) => (
                        <button
                          key={emp.id}
                          onClick={() => handleTestEmployee(emp.name)}
                          className="w-full flex items-center gap-2.5 px-2 py-2 rounded-md text-sm hover:bg-accent transition-colors text-left"
                        >
                          <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary flex-shrink-0">
                            {emp.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-foreground truncate text-sm">{emp.name}</p>
                            <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded-full font-medium ${roleColors[emp.role] || roleColors['Custom']}`}>
                              {emp.role}
                            </span>
                          </div>
                          {emp.is_primary && (
                            <span className="text-[10px] text-amber-600 font-medium">Primary</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* User Card — clickable avatar opens menu with Help, Notifications, Logout */}
          <div className="px-4 py-3">
            {userDisplayName && (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu(prev => !prev)}
                  className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-accent/70 transition-colors"
                >
                  {/* Avatar */}
                  <div className="relative h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center text-sm font-semibold text-primary flex-shrink-0">
                    {userInitials}
                    <div className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 bg-green-500 rounded-full border-2 border-muted"></div>
                    {/* Notification dot on avatar */}
                    {unreadCount > 0 && (
                      <div className="absolute -top-0.5 -right-0.5 h-4 w-4 bg-red-500 rounded-full flex items-center justify-center">
                        <span className="text-[9px] font-bold text-white">{unreadCount > 9 ? '9+' : unreadCount}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-sm font-medium text-foreground truncate">{userDisplayName}</p>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-muted-foreground">Online</span>
                      {isFoundingMember && (
                        <FoundingMemberBadge className="text-[10px] px-1.5 py-0" />
                      )}
                    </div>
                  </div>
                  <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
                </button>

                {/* User Menu Popover */}
                {showUserMenu && (
                  <div className="absolute bottom-full left-0 right-0 mb-2 rounded-lg border border-border bg-background shadow-lg z-50 overflow-hidden">
                    <div className="py-1">
                      {helpFeedbackHref && (
                        <a
                          href={helpFeedbackHref}
                          onClick={() => setShowUserMenu(false)}
                          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-foreground hover:bg-accent transition-colors"
                        >
                          <HelpCircle className="h-4 w-4 text-muted-foreground" />
                          Help & Feedback
                        </a>
                      )}
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate('/dashboard/account');
                          handleNavClick();
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-foreground hover:bg-accent transition-colors"
                      >
                        <Bell className="h-4 w-4 text-muted-foreground" />
                        Notifications
                        {unreadCount > 0 && (
                          <Badge variant="destructive" className="ml-auto text-[10px] h-5 px-1.5">{unreadCount}</Badge>
                        )}
                      </button>
                      <div className="border-t border-border my-1" />
                      <button
                        onClick={handleLogoutClick}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors"
                      >
                        <LogOut className="h-4 w-4" />
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
